<script setup lang="ts">
import { computed } from 'vue'

import { useCreditGuard } from '../../composables/useCreditGuard'
import { ORPHEUS_LOW_CREDITS } from '../../service/creditPresentation'
import { useCreditStore } from '../../stores/creditStore'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Icon } from '@/shared/ui'

const { t } = useI18n()
const creditStore = useCreditStore()
const creditGuard = useCreditGuard()

const locked = computed(() => creditStore.isLocked)
const low = computed(
  () =>
    creditStore.hasBalance &&
    !creditStore.isTrial &&
    !locked.value &&
    creditStore.remaining <= ORPHEUS_LOW_CREDITS,
)
const visible = computed(() => locked.value || low.value)
</script>

<template>
  <Transition name="orpheus-credit-hint">
    <Alert
      v-if="visible"
      class="orpheus-credit-hint"
      :tone="locked ? 'danger' : 'warning'"
      :title="locked ? t('credits.depleted.title') : ''"
    >
      <div class="orpheus-credit-hint__body flex items-center justify-between gap-md">
        <span class="orpheus-credit-hint__message flex items-center gap-xs">
          <Icon :name="locked ? 'alert-triangle' : 'bolt'" :size="16" aria-hidden="true" />
          <span v-if="locked">{{ t('credits.orpheus.locked') }}</span>
          <span v-else>
            {{
              t('credits.orpheus.low', {
                count: creditStore.remaining,
              })
            }}
          </span>
        </span>
        <Button :variant="locked ? 'danger' : 'outline'" size="xs" @click="creditGuard.openUpgrade">
          {{ t('credits.panel.upgrade') }}
        </Button>
      </div>
    </Alert>
  </Transition>
</template>

<style scoped>
.orpheus-credit-hint :deep(.orpheus-credit-hint-enter-active) {
  transition-property: opacity, transform;
  transition-timing-function: ease;
  transition-duration: 150ms;
}
.orpheus-credit-hint :deep(.orpheus-credit-hint-leave-active) {
  transition-property: opacity, transform;
  transition-timing-function: ease;
  transition-duration: 150ms;
}
.orpheus-credit-hint :deep(.orpheus-credit-hint-enter-from) {
  opacity: 0%;
  transform: translateY(var(--ui-space-xs));
}
.orpheus-credit-hint :deep(.orpheus-credit-hint-leave-to) {
  opacity: 0%;
  transform: translateY(var(--ui-space-xs));
}
</style>
